import { useRef, useCallback } from 'react';
import { cn } from '@/lib/cn';
import { isLineType } from '../lib/lineMath';

interface TimelineTask {
  id: string;
  timestamp?: string | number;
  taskType?: string;
}

interface TimelineProps {
  currentTime: number;
  duration: number;
  onSeek: (seconds: number) => void;
  tasks?: TimelineTask[];
  selectedTaskId?: string;
  onSelectTask?: (task: TimelineTask) => void;
}

export function Timeline({
  currentTime,
  duration,
  onSeek,
  tasks = [],
  selectedTaskId,
  onSelectTask,
}: TimelineProps) {
  const barRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!barRef.current || !duration) return;
      const rect = barRef.current.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      onSeek(fraction * duration);
    },
    [duration, onSeek],
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="group relative w-full px-1">
      {/* Clickable track */}
      <div
        ref={barRef}
        className="relative h-2 cursor-pointer rounded-full bg-white/30 transition-[height] group-hover:h-3"
        onClick={handleClick}
      >
        {/* Progress fill */}
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-white/80"
          style={{ width: `${progress}%` }}
        />

        {/* Task markers */}
        {tasks.map((task) => {
          if (task.timestamp == null || !duration) return null;
          const pos = (Number(task.timestamp) / 1000 / duration) * 100;
          const isActive = task.id === selectedTaskId;
          const isArrow = task.taskType ? isLineType(task.taskType) : false;

          return (
            <button
              key={task.id}
              type="button"
              className={cn(
                'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-transform hover:scale-150',
                isActive ? 'size-3 ring-2 ring-white' : 'size-2',
                isArrow ? 'bg-destructive' : 'bg-primary',
              )}
              style={{ left: `${pos}%` }}
              onClick={(e) => {
                e.stopPropagation();
                onSelectTask?.(task);
              }}
              title={`Task ${task.id}`}
            />
          );
        })}
      </div>
    </div>
  );
}
